import { useCallback, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store'
import { useAuth } from '@/contexts/AuthContext'
import {
  fetchFolderTree,
  createFolder as createFolderThunk,
  updateFolder as updateFolderThunk,
  deleteFolder as deleteFolderThunk,
  moveFolder as moveFolderThunk,
  optimisticMoveFolder,
  optimisticDeleteFolder,
} from '@/store/slices/foldersSlice'
import {
  fetchFolderFiles,
  moveFile as moveFileThunk,
  moveMultipleFiles as moveMultipleFilesThunk,
  optimisticMoveFile,
  optimisticMoveMultipleFiles,
} from '@/store/slices/filesSlice'
import {
  selectFile,
  selectFolder,
  toggleFileSelection,
  selectAllFiles,
  clearFileSelection,
  selectFileRange,
  selectSingleFile,
  toggleFolderExpansion,
  setFolderExpanded,
  saveNavigationForSearch,
  restoreNavigationFromSearch,
  navigateToRoot,
} from '@/store/slices/uiSlice'
import { selectFilesLoading, selectNavigationState } from '@/store/selectors'
import { Folder, FileItem } from '@/types'
import {
  navigateToFolderWithHash,
  getCurrentFolderPathFromHash,
  findFolderByPath,
  getParentIdsFromPath,
  onHashChange,
} from '@/utils/hashNavigation'

export function useNavigation() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()

  // Use memoized selector to prevent unnecessary rerenders
  const {
    folderTree,
    isLoadingTree,
    treeError,
    selectedFile,
    selectedFolder,
    selectedFiles,
    lastSelectedFileId,
    expandedFolders,
    navigationHistory,
    rootFiles,
    filesError,
    folderFiles,
  } = useAppSelector(selectNavigationState)

  // Loading states calculated from main selector
  const isLoadingFiles = useAppSelector((state) =>
    selectFilesLoading(state, selectedFolder?.id || 'root')
  )
  const isLoadingRootFiles = useAppSelector((state) => selectFilesLoading(state, 'root'))

  // Derived state with useMemo for expensive calculations
  const allFilesSelected = useMemo(() => {
    if (folderFiles.length === 0) return false
    return folderFiles.every((file) => selectedFiles.has(file.id))
  }, [folderFiles, selectedFiles])

  const someFilesSelected = useMemo(() => {
    if (folderFiles.length === 0) return false
    return folderFiles.some((file) => selectedFiles.has(file.id)) && !allFilesSelected
  }, [folderFiles, selectedFiles, allFilesSelected])

  // Load initial data when authenticated - REMOVED from hook
  // Data loading should be handled at app level, not in every hook instance

  // Actions
  const actions = {
    // File selection
    selectFile: useCallback(
      (file: FileItem | null) => {
        dispatch(selectFile(file))
      },
      [dispatch]
    ),

    // Folder selection with auto-expansion and hash navigation
    selectFolder: useCallback(
      (folder: Folder | null, updateHash: boolean = true) => {
        dispatch(selectFolder(folder))

        // Navigate to dashboard page
        navigate('/dashboard')

        // Update URL hash with folder path (this will happen after navigate)
        if (updateHash) {
          // Use setTimeout to ensure navigate completes first
          setTimeout(() => {
            navigateToFolderWithHash(folder)
          }, 0)
        }

        if (folder) {
          // Load files for the selected folder
          if (isAuthenticated) {
            dispatch(fetchFolderFiles(folder.id))
          }

          // Expand the folder itself and root
          dispatch(setFolderExpanded({ folderId: folder.id, expanded: true }))
          dispatch(setFolderExpanded({ folderId: 0, expanded: true })) // Root

          // Expand parent folders to show the path to this folder
          const parentIds = getParentIdsFromPath(folderTree, folder.path)
          parentIds.forEach((parentId) => {
            dispatch(setFolderExpanded({ folderId: parentId, expanded: true }))
          })
        }
      },
      [dispatch, navigate, isAuthenticated, folderTree]
    ),

    // Multi-file selection
    toggleFileSelection: useCallback(
      (fileId: number) => {
        dispatch(toggleFileSelection(fileId))
      },
      [dispatch]
    ),

    // Single file selection (clears others)
    selectSingleFile: useCallback(
      (fileId: number) => {
        dispatch(selectSingleFile(fileId))
      },
      [dispatch]
    ),

    // Range selection with Shift+click.
    // `orderedFileIds` is the order the table actually rendered — the component
    // owns the order, so this never has to guess at the current sort.
    selectFileRange: useCallback(
      (targetFileId: number, orderedFileIds?: number[]) => {
        const fileIds =
          orderedFileIds ?? (selectedFolder ? folderFiles : rootFiles).map((file) => file.id)

        const targetIndex = fileIds.indexOf(targetFileId)
        if (targetIndex === -1) return

        // If no lastSelectedFileId, just select the target file
        if (!lastSelectedFileId) {
          dispatch(selectSingleFile(targetFileId))
          return
        }

        const lastSelectedIndex = fileIds.indexOf(lastSelectedFileId)
        if (lastSelectedIndex === -1) {
          dispatch(selectSingleFile(targetFileId))
          return
        }

        // Calculate range
        const startIndex = Math.min(lastSelectedIndex, targetIndex)
        const endIndex = Math.max(lastSelectedIndex, targetIndex)
        const rangeFileIds = fileIds.slice(startIndex, endIndex + 1)

        // Merge with existing selection
        const selectedFilesArray = Array.from(selectedFiles)
        const newSelection = [...new Set([...selectedFilesArray, ...rangeFileIds])]

        dispatch(selectFileRange({ fileIds: newSelection, lastSelectedId: targetFileId }))
      },
      [dispatch, selectedFolder, folderFiles, rootFiles, selectedFiles, lastSelectedFileId]
    ),

    selectAllFiles: useCallback(() => {
      const fileIds = folderFiles.map((file) => file.id)
      dispatch(selectAllFiles(fileIds))
    }, [dispatch, folderFiles]),

    clearFileSelection: useCallback(() => {
      dispatch(clearFileSelection())
    }, [dispatch]),

    // Navigation
    navigateToParent: useCallback(() => {
      // This will be handled by the component that knows the parent folder
      // For now, just navigate to root
      dispatch(navigateToRoot())
    }, [dispatch]),

    navigateToRoot: useCallback(() => {
      dispatch(navigateToRoot())
    }, [dispatch]),

    // Folder expansion
    toggleFolderExpansion: useCallback(
      (folderId: number) => {
        dispatch(toggleFolderExpansion(folderId))
      },
      [dispatch]
    ),

    setFolderExpanded: useCallback(
      (folderId: number, expanded: boolean) => {
        dispatch(setFolderExpanded({ folderId, expanded }))
      },
      [dispatch]
    ),

    // Data refresh
    refreshFolderTree: useCallback(() => {
      if (isAuthenticated) {
        dispatch(fetchFolderTree())
      }
    }, [dispatch, isAuthenticated]),

    refreshFolderFiles: useCallback(() => {
      if (isAuthenticated) {
        if (selectedFolder) {
          dispatch(fetchFolderFiles(selectedFolder.id))
        } else {
          dispatch(fetchFolderFiles(null))
        }
      }
    }, [dispatch, isAuthenticated, selectedFolder]),

    refreshSpecificFolderFiles: useCallback(
      (folderId?: number) => {
        if (isAuthenticated) {
          if (folderId === undefined) {
            dispatch(fetchFolderFiles(null))
          } else {
            dispatch(fetchFolderFiles(folderId))
          }
        }
      },
      [dispatch, isAuthenticated]
    ),

    // Search navigation preservation
    saveNavigationForSearch: useCallback(() => {
      dispatch(saveNavigationForSearch())
    }, [dispatch]),

    restoreNavigationFromSearch: useCallback(() => {
      dispatch(restoreNavigationFromSearch())
    }, [dispatch]),

    // Folder management
    createFolder: useCallback(
      async (data: {
        name: string
        description?: string
        parent_id?: number
      }): Promise<Folder | null> => {
        if (!isAuthenticated) return null

        try {
          const result = await dispatch(createFolderThunk(data)).unwrap()
          return result.folder
        } catch (error) {
          console.error('Failed to create folder:', error)
          return null
        }
      },
      [dispatch, isAuthenticated]
    ),

    updateFolder: useCallback(
      async (
        folderId: number,
        data: { name?: string; description?: string }
      ): Promise<Folder | null> => {
        if (!isAuthenticated) return null

        try {
          const result = await dispatch(updateFolderThunk({ folderId, data })).unwrap()

          // No need to refresh tree - Redux state handles updates optimistically

          return result.folder
        } catch (error) {
          console.error('Failed to update folder:', error)
          return null
        }
      },
      [dispatch, isAuthenticated]
    ),

    deleteFolder: useCallback(
      async (folderId: number): Promise<boolean> => {
        if (!isAuthenticated) return false

        try {
          // Optimistic delete
          dispatch(optimisticDeleteFolder(folderId))

          // Navigate to root if we're deleting the selected folder
          if (selectedFolder?.id === folderId) {
            dispatch(navigateToRoot())
          }

          // Perform actual delete
          await dispatch(deleteFolderThunk(folderId)).unwrap()
          return true
        } catch (error) {
          console.error('Failed to delete folder:', error)
          // TODO: Revert optimistic delete on error
          return false
        }
      },
      [dispatch, isAuthenticated, selectedFolder]
    ),

    // Move operations with optimistic updates
    moveFolder: useCallback(
      async (folderId: number, targetParentId?: number): Promise<boolean> => {
        if (!isAuthenticated) return false

        try {
          // Optimistic move
          dispatch(optimisticMoveFolder({ folderId, targetParentId }))

          // Perform actual move
          await dispatch(moveFolderThunk({ folderId, targetParentId })).unwrap()

          // No need to refresh folder tree - Redux state handles this optimistically
          // No need to refresh folder files - Redux state is already updated

          return true
        } catch (error) {
          console.error('Failed to move folder:', error)
          // TODO: Revert optimistic move on error
          return false
        }
      },
      [dispatch, isAuthenticated]
    ),

    moveFile: useCallback(
      async (fileId: number, targetFolderId?: number): Promise<boolean> => {
        if (!isAuthenticated) return false

        try {
          // Use consistent keys for Redux state (null becomes 'root')
          const sourceFolderId = selectedFolder?.id || 'root'
          const targetKey = targetFolderId || 'root'

          // Optimistic move
          dispatch(
            optimisticMoveFile({
              fileId,
              targetFolderId: targetKey,
              sourceFolderId,
            })
          )

          // Perform actual move (API uses null for root)
          await dispatch(
            moveFileThunk({
              fileId,
              targetFolderId,
              sourceFolderId: selectedFolder?.id || null,
            })
          ).unwrap()

          // No need to refresh folder tree - Redux state handles this optimistically
          // No need to refresh folder files - Redux state is already updated

          return true
        } catch (error) {
          console.error('Failed to move file:', error)
          // TODO: Revert optimistic move on error
          return false
        }
      },
      [dispatch, isAuthenticated, selectedFolder]
    ),

    moveSelectedFiles: useCallback(
      async (targetFolderId?: number): Promise<boolean> => {
        if (!isAuthenticated || selectedFiles.size === 0) return false

        try {
          const fileIds = Array.from(selectedFiles)
          // Use consistent keys for Redux state (null becomes 'root')
          const sourceFolderId = selectedFolder?.id || 'root'
          const targetKey = targetFolderId || 'root'

          // Optimistic move
          dispatch(
            optimisticMoveMultipleFiles({
              fileIds,
              targetFolderId: targetKey,
              sourceFolderId,
            })
          )

          // Perform actual move (API uses null for root)
          await dispatch(
            moveMultipleFilesThunk({
              fileIds,
              targetFolderId,
              sourceFolderId: selectedFolder?.id || null,
            })
          ).unwrap()

          // Clear selection after successful move
          dispatch(clearFileSelection())

          // No need to refresh folder tree - Redux state handles this optimistically
          // No need to refresh folder files - Redux state is already updated

          return true
        } catch (error) {
          console.error('Failed to move files:', error)
          // TODO: Revert optimistic move on error
          return false
        }
      },
      [dispatch, isAuthenticated, selectedFiles, selectedFolder]
    ),

    // Navigate to folder from hash path (used on page load/refresh)
    navigateToFolderFromHash: useCallback(
      (folderPath: string) => {
        const targetFolder = findFolderByPath(folderTree, folderPath)

        // Use Redux action directly without navigation to preserve hash
        dispatch(selectFolder(targetFolder))

        if (targetFolder) {
          // Load files for the selected folder
          if (isAuthenticated) {
            dispatch(fetchFolderFiles(targetFolder.id))
          }

          // Expand the folder itself and root
          dispatch(setFolderExpanded({ folderId: targetFolder.id, expanded: true }))
          dispatch(setFolderExpanded({ folderId: 0, expanded: true })) // Root

          // Expand parent folders to show the path to this folder
          const parentIds = getParentIdsFromPath(folderTree, targetFolder.path)
          parentIds.forEach((parentId) => {
            dispatch(setFolderExpanded({ folderId: parentId, expanded: true }))
          })
        }
      },
      [dispatch, isAuthenticated, folderTree]
    ),
  }

  return {
    // State
    selectedFile,
    selectedFolder,
    selectedFiles,
    folderTree,
    folderFiles,
    rootFiles,
    isLoadingTree,
    isLoadingFiles,
    isLoadingRootFiles,
    treeError,
    filesError,
    expandedFolders,
    navigationHistory,

    // Computed state
    allFilesSelected,
    someFilesSelected,

    // Actions
    ...actions,
  }
}
